import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "../misc/Button";
import ConfirmPopup from "../misc/ConfirmPopup";
import { Input } from "../misc/Input";
import { Label } from "../misc/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../misc/Select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../misc/Tabs";
import { Textarea } from "../misc/Textarea";
import { ContentAudience } from "../../types/contentAudience";
import { cn } from "../../utils/classMerger";
import { US_STATES } from "../../utils/states";
import InteractiveAiUsageBar from "./InteractiveAiUsageBar";
import NicheSetupPrompt from "./NicheSetupPrompt";
import {
    addOwnTopic, getNicheScript, getNicheTopics, refreshNicheTopics, searchLocal, topicsGeneratedThisSession,
} from "./nicheApi";
import { describeNicheError, refillText } from "./nicheErrors";
import { NicheOverview, NichePlace, NicheScript, NicheTopic, voiceOf } from "./types";

export interface NicheSelection {
    topicId: string | null;
    script: string;
}

interface NicheTopicScriptStepProps {
    overview: NicheOverview | null;
    refreshOverview: () => Promise<void>;
    value: NicheSelection;
    // Takes an updater too (the wizard passes its state setter): a reply that lands
    // after the step closed must be applied to the selection as it is by then.
    onChange: (next: React.SetStateAction<NicheSelection>) => void;
    // True while a request is in flight: the wizard holds Next so a late script
    // can't land after the agent moved on (or swap the teleprompter mid-take).
    onBusyChange?: (busy: boolean) => void;
    // A recorded take was read from the picked topic's script, so picking another asks first.
    hasRecording?: boolean;
    onDiscardRecording?: () => void;
    // The wizard's voice; own topics and scripts are checked and written in it.
    contentAudience: ContentAudience;
}

type Tab = "niche" | "local" | "own";
type Busy = null | "topics" | "script" | "local" | "own";

const TABS: Array<[Tab, string]> = [
    ["niche", "For my niche"],
    ["local", "Happening locally"],
    ["own", "My own topic"],
];
const FAIR_HOUSING_NOTE = "You're responsible for what you say on camera. Speak to situations, not to who should live where.";
const OTHER_TOWN = "other";

// Mirrors the api's estimate: 150 words a minute.
export const estimateScriptSeconds = (text: string): number =>
    Math.round(text.trim().split(/\s+/).filter(Boolean).length / 2.5);

const placeKey = (p: NichePlace): string => `${p.town.trim().toLowerCase()}|${p.state.toUpperCase()}`;
const uniquePlaces = (list: NichePlace[]): NichePlace[] =>
    list.filter((p, i) => p.town.trim() && list.findIndex((q) => placeKey(q) === placeKey(p)) === i);
const eventDay = (date: string): string =>
    new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

// My Niche Video, step 2 (spec §4.3): pick a topic, get an editable script.
const NicheTopicScriptStep: React.FC<NicheTopicScriptStepProps> = ({
    overview, refreshOverview, value, onChange, onBusyChange, hasRecording, onDiscardRecording, contentAudience,
}) => {
    const [tab, setTab] = useState<Tab>("niche");
    const [confirmTopic, setConfirmTopic] = useState<NicheTopic | null>(null);
    const [topics, setTopics] = useState<NicheTopic[]>([]);
    const [scripts, setScripts] = useState<Record<string, NicheScript>>({});
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState<Busy>(null);
    const [error, setError] = useState<string | null>(null);
    const [exhausted, setExhausted] = useState<{ resetsOn: string | null } | null>(null);
    const [ownText, setOwnText] = useState("");
    const [area, setArea] = useState("0");
    const [otherTown, setOtherTown] = useState("");
    const [otherState, setOtherState] = useState("");
    const [localResults, setLocalResults] = useState<NicheTopic[] | null>(null);
    const [searchedTown, setSearchedTown] = useState("");
    const autoGenerated = useRef(false);
    // Seeded from the selection: the wizard unmounts this step, so Back remounts it mid-selection.
    const pickRef = useRef<string | null>(value.topicId);
    // Handlers outlive their render (they await), so they read the selection as it is now.
    const valueRef = useRef(value);
    valueRef.current = value;
    const onBusyChangeRef = useRef(onBusyChange);
    onBusyChangeRef.current = onBusyChange;
    const isBusy = busy !== null;
    // A reply can land after the agent left the step (Back, then maybe a new pick
    // in a fresh mount); it may then only fill a script that is still missing.
    const mountedRef = useRef(true);
    // Only this mount's first read may fill an empty script from the saved one; later
    // re-reads (local search, a vanished topic) must not undo a script the agent cleared.
    const firstLoadRef = useRef(true);

    useEffect(() => {
        onBusyChangeRef.current?.(isBusy);
    }, [isBusy]);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            // Leaving the step mid-request must not strand the wizard's Next.
            onBusyChangeRef.current?.(false);
        };
    }, []);

    const select = (next: NicheSelection): void => {
        if (mountedRef.current) onChange(next);
    };
    // Fills `topicId`'s script only while it is still the pick and has none, so a new
    // pick is never flipped back and nothing the agent typed is replaced.
    const fillScript = (topicId: string, text: string): void =>
        onChange((prev) => (prev.topicId === topicId && !prev.script.trim() ? { topicId, script: text } : prev));

    const profile = overview?.profile ?? null;
    const hasProfile = !!profile;
    const allowance = overview?.allowance;
    const paused = !!exhausted || !!allowance?.paused;
    const resetsOn = exhausted?.resetsOn ?? allowance?.resetsOn ?? null;
    const canGenerate = !paused && busy === null;
    // Like the api, a saved script counts only in the voice it was written in;
    // while paused, any saved one beats a blank page.
    const usableSaved = (s: NicheScript | undefined): NicheScript | undefined =>
        s && (paused || voiceOf(s) === contentAudience) ? s : undefined;
    // Niche ideas and local items belong to the voice they were made for; own topics to both. The
    // voice is picked on an earlier step and the wizard unmounts this one in between, so it is
    // fixed for the life of a mount.
    const inVoice = (t: NicheTopic): boolean => voiceOf(t) === contentAudience;
    const places = profile ? uniquePlaces([profile.homeBase, ...profile.serviceAreas]) : [];
    const homeState = profile?.homeBase.state ?? "";
    // "Another town…" starts on the home-base state; a state the agent picked is kept.
    useEffect(() => {
        if (homeState && !otherState) setOtherState(homeState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeState]);

    const clearIfGone = (list: NicheTopic[]): void => {
        const { topicId } = valueRef.current;
        if (!topicId || list.some((t) => t.id === topicId) || !mountedRef.current) return;
        select({ topicId: null, script: "" });
        // A take read from a topic that's gone can't be posted for it.
        if (hasRecording) onDiscardRecording?.();
    };

    const fail = (err: unknown): void => {
        const view = describeNicheError(err);
        if (view.exhausted) {
            setExhausted(view.exhausted);
            setError(null);
            void refreshOverview();
            return;
        }
        setError(view.text);
        if (view.profileRequired) void refreshOverview();
        if (view.topicGone) void loadTopics().then((list) => list && clearIfGone(list));
    };

    const loadTopics = async (): Promise<NicheTopic[] | null> => {
        try {
            const firstLoad = firstLoadRef.current;
            firstLoadRef.current = false;
            const res = await getNicheTopics();
            setTopics(res.topics);
            setScripts(Object.fromEntries(res.scripts.map((s) => [s.topicId, s])));
            // Reopened with a pick but no script (Back while it was written): use the saved one.
            const { topicId, script: text } = valueRef.current;
            const saved = firstLoad && topicId && !text.trim()
                ? usableSaved(res.scripts.find((s) => s.topicId === topicId))
                : undefined;
            if (saved && mountedRef.current) fillScript(saved.topicId, saved.text);
            return res.topics;
        } catch (err) {
            fail(err);
            return null;
        } finally {
            setLoaded(true);
        }
    };

    const moreIdeas = async (): Promise<void> => {
        setBusy("topics");
        setError(null);
        try {
            const list = await refreshNicheTopics(contentAudience);
            setTopics(list);
            clearIfGone(list);
            void refreshOverview();
        } catch (err) {
            fail(err);
        } finally {
            setBusy(null);
        }
    };

    useEffect(() => {
        if (!hasProfile) return;
        let active = true;
        void (async () => {
            const list = await loadTopics();
            if (!active || !list || list.some((t) => t.source === "niche" && inVoice(t))) return;
            // A generation for this voice already succeeded this session (the step reopened as it
            // finished, and this GET was served before it saved): re-read, never re-generate.
            if (topicsGeneratedThisSession(contentAudience)) {
                setLoaded(false); // "Finding topics for you…" until the re-read lands
                await loadTopics();
                return;
            }
            // Each voice's ideas are generated on its first use, then kept (spec §4.3).
            if (!autoGenerated.current && !paused) {
                autoGenerated.current = true;
                await moreIdeas();
            }
        })();
        return () => {
            active = false;
        };
        // Loads once per profile; the handlers read the latest state when they run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasProfile]);

    const writeScript = async (topicId: string, rewrite: boolean): Promise<void> => {
        setBusy("script");
        setError(null);
        try {
            const s = await getNicheScript(topicId, rewrite, contentAudience);
            setScripts((prev) => ({ ...prev, [topicId]: s }));
            if (!mountedRef.current) fillScript(topicId, s.text);
            else if (pickRef.current === topicId) onChange({ topicId, script: s.text });
            void refreshOverview();
        } catch (err) {
            fail(err);
        } finally {
            setBusy(null);
        }
    };

    const pick = async (topic: NicheTopic, confirmed = false): Promise<void> => {
        setError(null);
        // Tapping the selected topic again keeps the agent's script (Rewrite is the way to a new
        // one). With no script yet, e.g. after a failed write, the tap still retries.
        if (valueRef.current.topicId === topic.id && valueRef.current.script.trim()) return;
        if (hasRecording && !confirmed && valueRef.current.topicId !== topic.id) {
            setConfirmTopic(topic);
            return;
        }
        pickRef.current = topic.id;
        const usable = usableSaved(scripts[topic.id]);
        select({ topicId: topic.id, script: usable?.text ?? "" });
        // Paused: select it anyway so the agent can write their own script.
        if (usable || paused) return;
        await writeScript(topic.id, false);
    };

    const confirmPick = (): void => {
        const topic = confirmTopic;
        setConfirmTopic(null);
        if (!topic) return;
        onDiscardRecording?.();
        void pick(topic, true);
    };

    const addOwn = async (): Promise<void> => {
        const text = ownText.trim();
        if (!text) return;
        setBusy("own");
        setError(null);
        let topic: NicheTopic;
        try {
            topic = await addOwnTopic(text, contentAudience);
        } catch (err) {
            fail(err);
            setBusy(null);
            return;
        }
        setBusy(null);
        setTopics((prev) => [...prev, topic]);
        setOwnText("");
        void refreshOverview();
        // Left the step meanwhile: don't pick it, or write (and charge for) its script.
        if (!mountedRef.current) return;
        await pick(topic);
    };

    const findLocal = async (): Promise<void> => {
        const place = area === OTHER_TOWN ? { town: otherTown.trim(), state: otherState } : places[Number(area)];
        if (!place?.town || !place.state) {
            setError("Enter a town and pick a state.");
            return;
        }
        setBusy("local");
        setError(null);
        try {
            const res = await searchLocal(place.town, place.state, contentAudience);
            setLocalResults(res.topics);
            setSearchedTown(place.town);
            void refreshOverview();
            // The search replaced the stored local topics; re-read the list.
            const list = await loadTopics();
            if (list) clearIfGone(list);
        } catch (err) {
            fail(err);
        } finally {
            setBusy(null);
        }
    };

    if (!overview) return <p className="text-sm text-gray-500">Loading…</p>;

    // Basic Info already holds the agent there; this is the backstop.
    if (!profile) return <NicheSetupPrompt onRecheck={refreshOverview} />;

    const nicheTopics = topics.filter((t) => t.source === "niche" && inVoice(t));
    const ownTopics = topics.filter((t) => t.source === "own");
    const localShown = localResults ?? topics.filter((t) => t.source === "local" && !t.filmedAt && inVoice(t));
    const selected = topics.find((t) => t.id === value.topicId) ?? localShown.find((t) => t.id === value.topicId);
    const seconds = estimateScriptSeconds(value.script);

    const topicButton = (t: NicheTopic): JSX.Element => (
        <li key={t.id}>
            <button
                type="button"
                aria-pressed={value.topicId === t.id}
                disabled={busy !== null}
                onClick={() => void pick(t)}
                className={cn(
                    "w-full rounded-md border px-3 py-2 text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed",
                    value.topicId === t.id ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-400",
                )}
            >
                <span>{t.text}</span>
                {t.filmedAt && (
                    <span className="ml-2 rounded-full bg-[#E9C468] px-2 py-1 text-xs font-bold text-[#193456] shadow-sm">Filmed</span>
                )}
            </button>
        </li>
    );

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-bold text-primary text-center">Topic &amp; Script</h2>
            {allowance && <InteractiveAiUsageBar allowance={allowance} />}
            {paused && <p className="text-sm text-[#d23600]">{`${refillText(resetsOn)} Saved topics and scripts still work.`}</p>}

            <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
                <TabsList aria-label="Topic source">
                    {TABS.map(([id, label]) => (
                        <TabsTrigger key={id} value={id}>
                            {label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="niche" className="space-y-2">
                    {!loaded || (busy === "topics" && nicheTopics.length === 0) ? (
                        <p className="text-sm text-gray-500">Finding topics for you…</p>
                    ) : nicheTopics.length === 0 ? (
                        <p className="text-sm text-gray-500">No topics yet.</p>
                    ) : (
                        <ul className="space-y-2">{nicheTopics.map(topicButton)}</ul>
                    )}
                    <Button type="button" size="sm" disabled={!canGenerate} onClick={() => void moreIdeas()}>
                        {busy === "topics" ? "Thinking…" : <><Plus aria-hidden="true" /> More ideas</>}
                    </Button>
                </TabsContent>

                <TabsContent value="local" className="space-y-2">
                    <Label htmlFor="niche-local-area" className="block text-primary font-semibold">Area</Label>
                    <Select value={area} onValueChange={setArea}>
                        <SelectTrigger id="niche-local-area" aria-label="Area">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {places.map((p, i) => (
                                <SelectItem key={placeKey(p)} value={String(i)}>
                                    {`${p.town}, ${p.state}${i === 0 ? " (home base)" : ""}`}
                                </SelectItem>
                            ))}
                            <SelectItem value={OTHER_TOWN}>Another town…</SelectItem>
                        </SelectContent>
                    </Select>
                    {area === OTHER_TOWN && (
                        <div className="flex gap-2">
                            <Input aria-label="Town" maxLength={60} value={otherTown} onChange={(e) => setOtherTown(e.target.value)} />
                            <Select value={otherState} onValueChange={setOtherState}>
                                <SelectTrigger aria-label="State" className="w-28 shrink-0">
                                    <SelectValue placeholder="State" />
                                </SelectTrigger>
                                <SelectContent>
                                    {US_STATES.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <Button type="button" size="sm" disabled={!canGenerate} onClick={() => void findLocal()}>
                        {busy === "local" ? "Searching…" : "Find local topics"}
                    </Button>
                    {localResults && localResults.length === 0 && (
                        <p className="text-sm text-gray-600">{`Nothing we could use near ${searchedTown} in the next two weeks. Try another of your areas.`}</p>
                    )}
                    {!localResults && localShown.length > 0 && <p className="text-xs text-gray-500">From your last search</p>}
                    <ul className="space-y-2">
                        {localShown.map((t) => (
                            <li key={t.id} className="space-y-1 rounded-md border border-gray-200 p-3">
                                <p className="font-semibold text-primary">{t.text}</p>
                                {t.local && (
                                    <>
                                        <p className="text-xs text-gray-600">{`${eventDay(t.local.date)} · ${t.local.town}`}</p>
                                        <p className="text-sm">{t.local.whyItFits}</p>
                                        <a href={t.local.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs underline">
                                            Source
                                        </a>
                                    </>
                                )}
                                <div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={value.topicId === t.id ? "default" : "outline"}
                                        disabled={busy !== null}
                                        onClick={() => void pick(t)}
                                    >
                                        {value.topicId === t.id ? "Selected" : "Use this"}
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </TabsContent>

                <TabsContent value="own" className="space-y-2">
                    <Textarea
                        aria-label="Your topic"
                        maxLength={200}
                        rows={2}
                        value={ownText}
                        onChange={(e) => setOwnText(e.target.value)}
                        placeholder="e.g. Should you fix the roof before listing?"
                    />
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{ownText.length}/200</span>
                        <Button type="button" size="sm" disabled={!canGenerate || !ownText.trim()} onClick={() => void addOwn()}>
                            {busy === "own" ? "Checking…" : "Use this topic"}
                        </Button>
                    </div>
                    {ownTopics.length > 0 && <ul className="space-y-2">{ownTopics.map(topicButton)}</ul>}
                </TabsContent>
            </Tabs>

            {error && <p role="alert" className="text-sm text-red-500">{error}</p>}

            {value.topicId && (
                <div className="space-y-2 rounded-md border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-primary">Your script</h3>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!canGenerate}
                            onClick={() => void writeScript(value.topicId as string, true)}
                        >
                            {busy === "script" ? "Writing…" : "Rewrite"}
                        </Button>
                    </div>
                    {selected && <p className="text-sm text-gray-600">{selected.text}</p>}
                    <Textarea
                        aria-label="Script"
                        rows={7}
                        value={value.script}
                        disabled={busy === "script"}
                        placeholder={paused ? "Write your script here." : undefined}
                        onChange={(e) => onChange({ ...value, script: e.target.value })}
                    />
                    <p className="text-xs text-gray-500">{`About ${seconds} second${seconds === 1 ? "" : "s"}`}</p>
                </div>
            )}

            <p className="text-xs text-gray-500">{FAIR_HOUSING_NOTE}</p>

            <ConfirmPopup
                open={confirmTopic !== null}
                title="Change topic?"
                message="You already recorded a video for this topic. If you change it, that video is removed and you'll record a new one."
                confirmText="Change topic"
                cancelText="Keep my video"
                onConfirm={confirmPick}
                onCancel={() => setConfirmTopic(null)}
            />
        </div>
    );
};

export default NicheTopicScriptStep;
